import React, { useEffect, useMemo, useRef } from 'react';

import { isMainSearchTypeKeywords } from '../../util/search';
import {
  searchPageHasUsableLocation,
  getUsableServiceAddressPlaceFromUser,
} from '../../util/searchPageLocationGate';
import { applyLocationToSearchHistory } from './SearchPage.shared';

/**
 * Enforces a usable search location on /s for location-based search: syncs URL from profile when
 * possible, otherwise passes needsLocationPicker so the page shows an inline address picker.
 */
const SearchPageLocationGate = props => {
  const {
    config,
    history,
    location,
    routeConfiguration,
    currentUser,
    authInfoLoaded,
    children,
  } = props;

  const keywordSearch = isMainSearchTypeKeywords(config);
  const locationSearch = location.search;
  const locationPathname = location.pathname;
  const hasUrl = searchPageHasUsableLocation(config, locationSearch);

  const profilePlace = useMemo(() => {
    if (keywordSearch || hasUrl) {
      return null;
    }
    return getUsableServiceAddressPlaceFromUser(currentUser);
  }, [keywordSearch, hasUrl, currentUser]);

  const appliedProfileKeyRef = useRef(null);

  useEffect(() => {
    if (keywordSearch || hasUrl || !profilePlace) {
      if (hasUrl) {
        appliedProfileKeyRef.current = null;
      }
      return;
    }

    const profileKey = [
      profilePlace.search,
      profilePlace.selectedPlace?.address,
      profilePlace.selectedPlace?.bounds?.ne?.lat,
      profilePlace.selectedPlace?.bounds?.sw?.lat,
    ].join('|');

    if (appliedProfileKeyRef.current === profileKey) {
      return;
    }
    appliedProfileKeyRef.current = profileKey;

    applyLocationToSearchHistory({
      history,
      location,
      routeConfiguration,
      config,
      autocompleteValue: profilePlace,
      replace: true,
    });
  }, [
    keywordSearch,
    hasUrl,
    profilePlace,
    history,
    locationSearch,
    locationPathname,
    routeConfiguration,
    config,
  ]);

  const needsLocationPicker =
    authInfoLoaded && !keywordSearch && !hasUrl && !profilePlace;

  return React.cloneElement(children, { needsLocationPicker });
};

export default SearchPageLocationGate;
